import { Box, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import ObservationRateGraph from '../Graphs/ObservationRateGraph';
import { ReactComponent as InfoIcon } from '../../assets/img/info-icon.svg';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorState } from '../common/ErrorState';
import { NoDataState } from '../common/NoDataState';

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '1000px',
  maxWidth: '100%',
  minHeight: '500px',
  padding: '32px',
  borderRadius: '16px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(229, 231, 235, 0.8)',
  background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
  overflow: 'hidden',
}));

const StyledHeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '23px',
  padding: '20px',
  marginBottom: '20px',
}));

const StyledIconColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '11px',
  alignItems: 'center',
  paddingTop: '7px',
}));

const StyledVerticalLine = styled(Box)(({ theme }) => ({
  borderLeft: '2px solid #45009C',
  height: '50px',
}));

const StyledContentColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: '#17052D',
  marginBottom: '8px',
}));

const StyledDescription = styled(Typography)(({ theme }) => ({
  color: '#8B8296',
  fontSize: '14px',
}));

const StyledGraphContainer = styled(Box)(({ theme }) => ({
  width: '100%',
}));

export const ObservationRate = (props: any) => {
  const { isLoading, isError, error, hasData, data } = props;

  // Show loading state
  if (isLoading) {
    return (
      <StyledPaper className="custom-chart">
        <LoadingSpinner 
          message="Loading observation rate data..." 
          height="500px"
          showBackground={false}
        />
      </StyledPaper>
    );
  }

  // Show error state
  if (isError) {
    return (
      <StyledPaper className="custom-chart">
        <ErrorState 
          title="Failed to load observation rate data"
          error={error}
          height="500px"
          showBackground={false}
          onRetry={props.onRetry}
        />
      </StyledPaper>
    );
  }

  // Show no data state
  if (!hasData) {
    return (
      <StyledPaper className="custom-chart">
        <NoDataState 
          title="No observation rate data available"
          message="No bird detection data found for the selected date range and filters. Try adjusting your selection criteria."
          height="500px"
          showBackground={false}
        />
      </StyledPaper>
    );
  }

  return (
    <StyledPaper className="custom-chart">
      <StyledHeaderContainer>
        <StyledIconColumn>
          <InfoIcon />
          <StyledVerticalLine />
        </StyledIconColumn>
        <StyledContentColumn>
          <StyledTitle variant="h6">
            Observation rate
          </StyledTitle>
          <StyledDescription variant="body2">
            This graph shows the observation rate, defined as the number of bird observations per analyzed hour. The data is automatically aggregated to daily, weekly, or monthly intervals depending on the selected time range.
          </StyledDescription>
        </StyledContentColumn>
      </StyledHeaderContainer>
      <StyledGraphContainer id="observation-rate-graph">
        <ObservationRateGraph data={data} />
      </StyledGraphContainer>
    </StyledPaper>
  );
};
